import { QueryKey, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidResponsesQuery,
  BidResponsesRequest,
  PaginationOptions,
} from "../../../../generated/app-service-gql/graphql";
import { useGraphQlClient, useGraphQlQuery } from "../../useGraphQlClient";

type Filters = {
  pagination: PaginationOptions;
  textSearch?: string | null;
  stageIds?: string[] | null;
};

export function bidResponsesKey({
  resourceId,
  filters,
}: {
  resourceId: string | null | undefined;
  filters?: Filters;
}): QueryKey {
  const queryKey = ["bidResponses", { resourceId: resourceId ?? null }];

  if (filters) {
    queryKey.push(JSON.stringify(filters));
  }

  return queryKey;
}

const BID_RESPONSES_QUERY = graphql(`
  query BidResponses($request: BidResponsesRequest!, $pagination: PaginationOptions!) {
    bidResponses(BidResponsesRequest: $request, PaginationOptions: $pagination) {
      items {
        id
        bidId
        reference
        title
        section
        description
        weighting
        formattingRequirements
        response
        source {
          documentId
          title
          locations {
            pageNumber
            chunkNumber
            bbox {
              x
              y
              width
              height
            }
          }
        }
        teamId
        companyId
        stageId
        assignee {
          guid
          firstName
          lastName
        }
        reviewer {
          guid
          firstName
          lastName
        }
        dueDate
        bid {
          id
          name
        }
        createdAt
        updatedAt
        deletedAt
      }
      totalCount
    }
  }
`);

export type BidResponseDto = NonNullable<BidResponsesQuery["bidResponses"]["items"]>[0];

export function useBidResponses(
  request: BidResponsesRequest,
  pagination: PaginationOptions,
  options?: UseQueryOptions<BidResponsesQuery, unknown, BidResponsesQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    bidResponsesKey({
      resourceId: request.bidId,
      filters: { pagination, ...request },
    }),
    BID_RESPONSES_QUERY,
    [{ request, pagination }],
    options,
  );

  return {
    data: data?.bidResponses.items || [],
    totalCount: data?.bidResponses.totalCount || 0,
    ...rest,
  };
}

const EXPORT_PAGINATION = { page: 1, pageSize: 500 };

export function useFetchAllBidResponsesForExport() {
  const queryClient = useQueryClient();
  const { client } = useGraphQlClient();

  return async (
    request: BidResponsesRequest,
  ): Promise<{ items: BidResponseDto[]; isTruncated: boolean }> => {
    const data = await queryClient.fetchQuery({
      queryKey: bidResponsesKey({
        resourceId: request.bidId,
        filters: { pagination: EXPORT_PAGINATION, ...request },
      }),
      queryFn: () =>
        client.request(BID_RESPONSES_QUERY, { request, pagination: EXPORT_PAGINATION }),
      staleTime: 0,
    });
    const items = data.bidResponses.items ?? [];
    const isTruncated = data.bidResponses.totalCount > EXPORT_PAGINATION.pageSize;
    return { items, isTruncated };
  };
}
